/** Canonical source for parsing and formatting US comma-delimited address strings. Homed in `components/lib/`, the sanctioned non-presentational home: no flow reaches it, every consumer is a rendering-tier surface, and the body value-imports a utils sibling the utils/ import floor forbids. The space-delimited parser is NOT here: its one consumer is the SEO builder, so it lives at `apps/federa/src/lib/seo/address-space-delimited.ts`. */

import { trimmedNonBlank } from '@federa/const/line-splitting';
import { SENTINEL_ADDRESS_UNAVAILABLE } from '@federa/const/sentinels';
import { parseAddressParts } from '@federa/utils/strings/address-helpers';

interface ParsedAddress {
	street: string;
	city: string;
	state: string;
	zipCode?: string;
}

interface DisplayAddress {
	streetAddress: string;
	cityState: string;
}

/** The UNPARSED verdict: no comma structure was recoverable, so the whole input stands as the street and the locality
 *  fields stay empty rather than guessing. Named because both failure arms below reach it and a third would too. */
function streetOnly(street: string): ParsedAddress {
	return { street, city: '', state: '' };
}

export function parseAddress(addr: string): ParsedAddress {
	if (!addr) return streetOnly('');
	const parts = trimmedNonBlank(addr.split(','));
	if (parts.length === 0) return streetOnly(addr);
	const result = parseAddressParts(parts);
	if (!result.city && !result.state) {
		return streetOnly(addr);
	}
	return result;
}

export function cleanAddress(addr: string): string {
	if (!addr) return addr;
	return addr.replace(/,\s*\d{10,}\s*$/, '').trim();
}

export function parseAddressForDisplay(fullAddress: string): DisplayAddress {
	const cleaned = cleanAddress(fullAddress);
	if (!cleaned) return { streetAddress: SENTINEL_ADDRESS_UNAVAILABLE, cityState: '' };
	const parts = cleaned.split(',').map((p) => p.trim());
	if (parts.length >= 3) {
		const street = parts[0] ?? '';
		const city = parts[1] ?? '';
		const stateZip = parts[2] ?? '';
		const state: string = stateZip.replace(/\s+\d+.*$/, '');
		return { streetAddress: street, cityState: `${city}, ${state}` };
	} else if (parts.length === 2) {
		return { streetAddress: parts[0] ?? '', cityState: parts[1] ?? '' };
	}
	return { streetAddress: cleaned, cityState: '' };
}
